import { ILaneSignalPhaseInfo } from "@/types/map";

export default class CrossSignalPhase2D {
  constructor(params: {
    crossId: string;
    phaseLineLayer: __esri.FeatureLayer;
    phaseLabelLayer: __esri.FeatureLayer;
    sideWalkLayer: __esri.FeatureLayer;
    view: __esri.View;
  }) {
    this.crossId = params.crossId;
    this.phaseLineLayer = params.phaseLineLayer;
    this.phaseLabelLayer = params.phaseLabelLayer;
    this.sideWalkLayer = params.sideWalkLayer;
    this.view = params.view;
  }

  private view: __esri.View;
  private crossId: string;
  private phaseLineLayer: __esri.FeatureLayer;
  private phaseLabelLayer: __esri.FeatureLayer;
  private sideWalkLayer: __esri.FeatureLayer;
  private phaseLineGraphics: Map<string, __esri.Graphic> = new Map();
  private phaseLabelGraphics: Map<string, __esri.Graphic> = new Map();
  private sideWalkGraphics: Map<string, __esri.Graphic[]> = new Map();
  // 每个车道当前的灯色和时间
  private laneStates: Map<string, { color: string; time: number }> = new Map();

  private countDownTimer!: number;

  /** 此路口所有相位线设置成红色 */
  public async initializeCross() {
    this.stopLaneSignalPhase();

    // 相位线位置
    let query = this.phaseLineLayer.createQuery();
    query.where = `FEATUREID like '%${this.crossId}%'`;
    const lineGraphics = (await this.phaseLineLayer.queryFeatures(query))
      .features;
    lineGraphics.forEach((graphic) => {
      const isLock: string = graphic.attributes["LOCK"] || "";
      graphic.symbol = {
        type: "simple-line",
        color: isLock.toLowerCase() === "y" ? "#00C851" : "#ff4444",
        width: "6px",
        cap: "butt",
      } as any;
      this.phaseLineGraphics.set(graphic.attributes["FEATUREID"], graphic);
    });
    this.view.graphics.addMany(lineGraphics);

    // 人行横道线
    query = this.sideWalkLayer.createQuery();
    query.where = `CROSSID = '${this.crossId}'`;
    const sideWalkGraphics = (await this.sideWalkLayer.queryFeatures(query))
      .features;
    sideWalkGraphics.forEach((graphic) => {
      graphic.symbol = {
        type: "simple-fill",
        color: "white",
        outline: {
          color: "white",
        },
      } as any;
      const laneId = graphic.attributes["FEATUREID"];
      if (this.sideWalkGraphics.has(laneId)) {
        this.sideWalkGraphics.get(laneId)?.push(graphic);
      } else {
        this.sideWalkGraphics.set(laneId, [graphic]);
      }
    });
    this.view.graphics.addMany(sideWalkGraphics);

    // 倒计时标注位置
    query = this.phaseLabelLayer.createQuery();
    query.where = `FEATUREID like '%${this.crossId}%'`;
    const labelGraphics = (await this.phaseLabelLayer.queryFeatures(query))
      .features;
    labelGraphics.forEach((graphic) => {
      graphic.visible = false;
      this.phaseLabelGraphics.set(graphic.attributes["FEATUREID"], graphic);
    });
    this.view.graphics.addMany(labelGraphics);

    this.countDownTimer = setInterval(() => {
      this.countDown();
    }, 1000);
  }

  public setLaneSignalPhaseInfos(laneSignalPhaseInfos: ILaneSignalPhaseInfo[]) {
    laneSignalPhaseInfos.forEach((laneSignalPhaseInfo) => {
      const laneId = laneSignalPhaseInfo.laneIds;
      const time = laneSignalPhaseInfo.time;
      const colorString =
        laneSignalPhaseInfo.color.toLowerCase() === "r" ? "red" : "green";
      const color =
        laneSignalPhaseInfo.color.toLowerCase() === "r" ? "#ff4444" : "#00C851";

      this.laneStates.set(laneId, { color: colorString, time });

      const phaseLineGraphic = this.phaseLineGraphics.get(laneId);
      if (phaseLineGraphic) {
        phaseLineGraphic.symbol = {
          type: "simple-line",
          color,
          width: "6px",
          cap: "butt",
        } as any;
      }

      const laneSideWalkGraphics = this.sideWalkGraphics.get(laneId);
      if (laneSideWalkGraphics) {
        laneSideWalkGraphics.forEach((graphic) => {
          graphic.symbol = {
            type: "simple-fill",
            color: "white",
            outline: {
              color,
              width: "1px",
            },
          } as any;
        });
      }

      const phaseLabelGraphic = this.phaseLabelGraphics.get(laneId);
      if (phaseLabelGraphic) {
        if (colorString === "red") {
          phaseLabelGraphic.visible = true;
          phaseLabelGraphic.symbol = {
            type: "text",
            color: "white",
            haloColor: color,
            haloSize: "1px",
            text: time,
            font: {
              size: 16,
            },
          } as any;
        } else {
          phaseLabelGraphic.visible = false;
        }
      }
    });
  }

  private countDown() {
    // 倒计时时间标注
    this.phaseLabelGraphics.forEach((labelGraphic, laneId) => {
      const laneState = this.laneStates.get(laneId);
      if (laneState && laneState.time > 0) {
        laneState.time--;

        let color = laneState.color;
        // 绿灯时倒数5秒黄闪
        if (color === "green" && laneState.time <= 5) {
          color = laneState.time <= 2 ? "#f44336" : "#ffbb33";

          const phaseLineGraphic = this.phaseLineGraphics.get(laneId);
          if (phaseLineGraphic) {
            phaseLineGraphic.symbol = {
              type: "simple-line",
              color,
              width: "6px",
              cap: "butt",
            } as any;
          }

          const laneSideWalkGraphics = this.sideWalkGraphics.get(laneId);
          if (laneSideWalkGraphics) {
            laneSideWalkGraphics.forEach((graphic) => {
              graphic.symbol = {
                type: "simple-fill",
                color: "white",
                outline: {
                  color,
                  width: "1px",
                },
              } as any;
            });
          }
        }

        labelGraphic.symbol = {
          type: "text",
          color: "white",
          haloColor: color,
          haloSize: "1px",
          text: laneState.time,
          font: {
            size: 16,
          },
        } as any;
      }
    });
  }

  public stopLaneSignalPhase() {
    clearInterval(this.countDownTimer);
    this.view.graphics.removeMany([...this.phaseLineGraphics.values()]);
    this.view.graphics.removeMany([...this.phaseLabelGraphics.values()]);

    let sidewalkGraphics: Array<__esri.Graphic> = [];
    this.sideWalkGraphics.forEach((graphics, id) => {
      sidewalkGraphics = sidewalkGraphics.concat(graphics);
    });
    this.view.graphics.removeMany(sidewalkGraphics);

    this.phaseLineGraphics.clear();
    this.phaseLabelGraphics.clear();
    this.sideWalkGraphics.clear();
  }
}
